import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import Toast from "../components/shared/toast";

export type ToastType = "success" | "error" | "info" | "warning";

export type ToastOptions = {
  title: string;
  description?: string;
  type?: ToastType;
  duration?: number;
};

export type ToastRecord = ToastOptions & {
  id: number;
};

export type ShowToastFn = ((
  message: string,
  type?: ToastType,
  duration?: number
) => number) &
  ((options: ToastOptions) => number);

interface ToastContextValue {
  showToast: ShowToastFn;
  dismissToast: (id: number) => void;
  toasts: ToastRecord[];
}

const ToastContext = createContext<ToastContextValue | null>(null);

function buildToast(
  messageOrOptions: string | ToastOptions,
  type?: ToastType,
  duration?: number
): Omit<ToastRecord, "id"> & { duration: number } {
  if (typeof messageOrOptions === "string") {
    return {
      title: messageOrOptions,
      description: undefined,
      type: type ?? "info",
      duration: duration ?? 3000,
    };
  }

  const options = messageOrOptions;
  return {
    title: options.title,
    description: options.description,
    type: options.type ?? "info",
    duration: options.duration ?? 3000,
  };
}

export const ToastContextWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);

  const dismissToast = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    ((
      messageOrOptions: string | ToastOptions,
      type?: ToastType,
      duration?: number
    ) => {
      const toast = buildToast(messageOrOptions, type, duration);
      const id = Date.now() + Math.random();

      setToasts((current) => [
        ...current,
        {
          id,
          ...toast,
        },
      ]);

      if (toast.duration > 0) {
        window.setTimeout(() => dismissToast(id), toast.duration);
      }

      return id;
    }) as ShowToastFn,
    [dismissToast]
  );

  const value = useMemo(
    () => ({
      showToast,
      dismissToast,
      toasts,
    }),
    [showToast, dismissToast, toasts]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed bottom-4 right-4 z-50 flex max-w-sm flex-col gap-3">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            toast={toast}
            onClose={() => dismissToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToastContext = () => {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error(
      "useToastContext must be used within a ToastContextWrapper"
    );
  }

  return context;
};
